<template>
    <div class="session__edit">
        <div class="session__edit_header">
            <view-actions
                :breadcrumb-active="true"
                :breadcrumb-items="[
                    {
                        name: this.$t('ebs.sessions.edit.name.events'),
                        to: { name: 'ebs-event-list' }
                    },
                    {
                        name: sessionEventName,
                        to: sessionEventTo
                    }
                ]"
                :cancel-button-text="isSessionUpcoming || isSessionInProgress || isSessionDraft ? $t('ebs.sessions.edit.buttons.stop') : 'Back to session'"
                :cancel-notify-active="isSessionUpcoming || isSessionInProgress || isSessionDraft"
                :notify-title="$t('ebs.sessions.edit.title.session')"
                :notify-process="$t('ebs.sessions.edit.title.processStop')"
                :title="`${$t('ebs.sessions.edit.title.editing')} ${sessionDates}`"
                :trash-button-active="sessionAllowEdit && sessionAllowRemove"
                :submit-button-active="isSessionUpcoming || isSessionInProgress || isSessionDraft"
                :submit-button-text="'Update'"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-event-name="'session-update'"
                v-on="isSessionUpcoming || isSessionInProgress || isSessionDraft ? {} : { 'emit-cancel' : sessionStopEdit }"
                @emit-notify-remove-submit="sessionRemove"
                @emit-notify-cancel-submit="sessionStopEdit"
                @emit-submit="handleUpdate"
            />
        </div>
        <div class="session__edit_main">
            <div class="container fullheight">
                <div class="columns is-block-mobile fullheight m-t-b-0">
                    <div class="column is-narrow p-t-b-0">
                        <component-sidebar :nav-items="[
                            { label: $t('ebs.sessions.edit.labels.details'), name: 'ebs-session-edit-details' },
                            { label: $t('ebs.sessions.edit.labels.booking'), name: 'ebs-session-edit-bookings' },
                            { label: $t('ebs.sessions.edit.labels.cancellations'), name: 'ebs-session-edit-cancellations' },
                            { label: $t('ebs.sessions.edit.labels.feedback'), name: 'ebs-session-edit-completions' },
                            { label: $t('ebs.sessions.edit.labels.cost'), name: 'ebs-session-edit-costs' }
                        ]"></component-sidebar>
                    </div>
                    <div class="column custom--grid p-t-b-0">
                        <section class="session__edit_main_content">
                            <keep-alive>
                                <transition name="fade" mode="out-in">
                                    <router-view/>
                                </transition>
                            </keep-alive>
                        </section>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            :class="'session__edit_main_notifyhandlesubmit'"
            ref="popnotifyhandlesubmit"
            :title-text="'Save changes?'"
            :content-text="`There are ${sessionBookedLearners.length} users booked and ${sessionTrainers.length} trainer assigned to this session. Would you like to notify them of the changes?`"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Save changes'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            @emit-submit="performSubmitValidate"
        >
            <form-checkbox
                :disabled="!sessionHasBookings"
                :name="'session-edit-notify-learners'"
                :label="'Booked learners'"
                :value="sessionSendNotificationTo && sessionSendNotificationTo.includes('learners')"
                @input="setSessionSendNotificationTo('learners')"
            ></form-checkbox>
            <form-checkbox
                :name="'session-edit-notify-trainers'"
                :label="'Assigned trainers'"
                :value="sessionSendNotificationTo && sessionSendNotificationTo.includes('trainers')"
                @input="setSessionSendNotificationTo('trainers')"
            ></form-checkbox>
            <form-checkbox
                :disabled="!sessionHasSubscriptions"
                :name="'session-edit-notify-waitlist'"
                :label="'Waitlist'"
                :value="sessionSendNotificationTo && sessionSendNotificationTo.includes('waitlist')"
                @input="setSessionSendNotificationTo('waitlist')"
            ></form-checkbox>
            <form-notification
                :identifier="'session.updated'"
                :disabled="true"
                :name="'Session updated'"
            />
        </pop-notify>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>

import ViewActions from '@/components/default/shared/ViewActions'
import ComponentSidebar from '@/components/default/shared/ComponentSidebar'
import PopNotify from '@/components/default/shared/PopNotify'
import { validateBeforeRouteLeave, performSubmitValidate, performValidate } from '@/helpers/mixins'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormNotification from '@/components/default/forms/FormNotification'

export default {
  name: 'session-edit',
  data () {
    return {
      notifyLearners: false,
      notifyTrainers: false
    }
  },
  components: {
    FormNotification,
    FormCheckbox,
    PopNotify,
    ComponentSidebar,
    ViewActions
  },
  mixins: [validateBeforeRouteLeave, performSubmitValidate, performValidate],
  computed: {
    isSessionDraft () {
      return this.sessionStatus === 'draft'
    },
    isSessionUpcoming () {
      return this.sessionStatus === 'upcoming'
    },
    isSessionInProgress () {
      return this.sessionStatus === 'in_progress'
    },
    sessionStatus () {
      return this.$store.getters.getSessionDefaultStatus
    },
    sessionEventId () {
      return this.$store.getters['getEventId']
    },
    sessionEventName () {
      return this.$store.getters['getEventName']
    },
    sessionEventTo () {
      return { name: 'ebs-event-show', params: { id: this.sessionEventId } }
    },
    sessionDates () {
      const fromDate = this.$store.getters.getSessionFromDate
      const toDate = this.$store.getters.getSessionToDate
      //
      if (!fromDate || !toDate) return
      if (fromDate === toDate) {
        return new Date(fromDate).toLocaleDateString('en-gb', { year: 'numeric', month: 'short', day: '2-digit' })
      } else {
        return new Date(fromDate).toLocaleDateString('en-gb', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        }) + ' - ' + new Date(toDate).toLocaleDateString('en-gb', { year: 'numeric', month: 'short', day: '2-digit' })
      }
    },
    sessionAllowEdit () {
      return this.$store.getters.getSessionAllowEdit
    },
    sessionAllowRemove () {
      return this.$store.getters.getSessionAllowRemove
    },
    sessionHasBookings () {
      return this.$store.getters.getSessionHasBookedLearnersActive
    },
    sessionHasSubscriptions () {
      return this.$store.getters.getSessionHasSubscriptions
    },
    sessionBookedLearners () {
      return this.$store.getters.getSessionBookedLearners
    },
    sessionTrainers () {
      return this.$store.getters.getSessionTrainers
    },
    sessionSendNotificationTo () {
      return this.$store.getters.getSessionSendNotificationTo
    },
    //
    rootItemName () {
      return 'session'
    },
    nameActionUpdate () {
      return 'updateSession'
    }
  },
  methods: {
    performSubmitValidateValidationCallback () {
      this.$eventBus.$emit('stop-session-update')
    },
    performSubmitValidateFinallyCallback () {
      this.$eventBus.$emit('stop-session-update')
    },
    allowRedirectBeforeRouteLeave () {
      this.$eventBus.$emit('session-submit')
    },
    handleUpdate () {
      if (!this.isSessionDraft) {
        this.performValidate().then(() => {
          this.openNotifyHandleUpdate()
          this.$eventBus.$emit('stop-session-update')
        })
      } else {
        this.performSubmitValidate()
      }
    },
    openNotifyHandleUpdate () {
      this.$refs.popnotifyhandlesubmit.openNotify()
    },
    setSessionSendNotificationTo (data) {
      this.$store.dispatch('setSessionSendNotificationTo', data)
    },
    //
    sessionRemove () {
      this.allowRedirectBeforeRouteLeave()
      this.$store.dispatch('removeSession', this.$route.params.sessionId)
    },
    sessionStopEdit () {
      this.$store.dispatch('resetSession')
      this.allowRedirectBeforeRouteLeave()
      this.$router.push({ name: 'ebs-session-show', params: { id: this.$route.params.sessionId } })
    }
  },
  mounted () {
    this.$eventBus.$on('session-remove', this.sessionRemove)
  },
  beforeDestroy () {
    this.$eventBus.$off('session-remove')
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/sessions/sessionaddedit";
</style>
